/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Avatar, Tooltip, Typography, Badge, Row, Col, Space } from 'antd';
import { WarningOutlined, InfoOutlined, ExclamationOutlined, DownloadOutlined, CheckOutlined } from '@ant-design/icons';

// Misc imports
import { truncate } from '../../utils/string/truncate';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const baseClassName = '';

const NotificationAvatar = (props) => {
  const {
    notification,
    avatarSize = 45,
    wrapNotificationName = true,
    maxMessageCharacters = 85,
    className = '',
    ...restProps
  } = props;

  const [hasError, setHasError] = useState(false);

  const { t } = useTranslation();
  const { primaryColor } = useTheme();

  // maps the type of the notification
  // to the style and component to be displayed
  const iconFontSize = 'fs-xl';
  const styleMapping = {
    'SUCCESS': {
      STYLE: 'bg-success',
      ICON: <CheckOutlined className={iconFontSize} />
    },
    'WARNING': {
      STYLE: 'bg-warning',
      ICON: <WarningOutlined className={iconFontSize} />
    },
    'ERROR': {
      STYLE: 'bg-error',
      ICON: <ExclamationOutlined className={iconFontSize} />
    },
    'INFORM': {
      STYLE: 'bg-primary',
      ICON: <InfoOutlined className={iconFontSize} />
    },
    'DOWNLOAD': {
      STYLE: 'bg-success',
      ICON: <DownloadOutlined className={iconFontSize} />
    }
  };

  const badgeStyle = {
    position: 'absolute',
    left: -20,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 0
  };

  return (
    <span className={`${baseClassName} ${className}`} {...restProps}>
      <Row align="middle" justify="center">
        <Space align="start" size="middle">
          <Col>
            {
              !notification.read && (
                <Badge
                  dot
                  color={primaryColor}
                  style={badgeStyle}
                />
              )
            }

            <Avatar
              src={notification.iconUrl}
              shape="ellipsis"
              size={avatarSize}
              className={classNames('flex-none mt-1', {
                [styleMapping[notification.notificationType]?.STYLE || 'bg-black']: !notification.iconUrl || hasError,
                'bg-black': !notification.iconUrl || hasError
              })}
              onError={() => setHasError(true)}
            >
              {styleMapping[notification.notificationType]?.ICON}
            </Avatar>
          </Col>

          <Col>
            {
              notification?.message?.trim() ? (
                <Tooltip title={wrapNotificationName && notification.message.length > 80 && notification.message}>
                  <Typography.Text
                    ellipsis={!wrapNotificationName}
                    className={classNames('fw-400', {
                      'fw-bold': !notification.read
                    })}
                  >
                    {maxMessageCharacters ? truncate(notification.message, maxMessageCharacters, '...') : notification.message}
                  </Typography.Text>
                </Tooltip>
              ) : (
                <Typography.Text type="secondary" ellipsis>
                  {t('No message provided.')}
                </Typography.Text>
              )
            }
          </Col>
        </Space>
      </Row>
    </span>
  );
};

NotificationAvatar.propTypes = {
  notification: PropTypes.object,
  avatarSize: PropTypes.number,
  wrapNotificationName: PropTypes.bool,
  maxMessageCharacters: PropTypes.number,
  className: PropTypes.string
};

export default NotificationAvatar;
